import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Paper, Box, IconButton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ToastService from "@/service/toaster.service"; // Assuming you have a ToastService
import vendorAuthService from "@/service/auth.service/vendor.auth.service"; // Assuming you have vendor auth service
import { Visibility, VisibilityOff } from "@mui/icons-material"; // MUI Icons for eye button

function SetNewPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    complexity: false,
    strength: "Weak",
  });
  const [showPassword, setShowPassword] = useState(false); // State for showing password
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State for showing confirm password

  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state ? location.state.email : ""; // Retrieve email from location state

  // Password validation logic
  const validatePassword = (password) => {
    const lengthValid = password.length >= 8;
    const complexityValid = /[A-Za-z]/.test(password) && /\d/.test(password) && /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const strengthValid = password.length >= 8 && complexityValid;

    setPasswordValidations({
      length: lengthValid,
      complexity: complexityValid,
      strength: strengthValid ? "Strong" : "Weak",
    });
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);

    // Clear error if passwords match while typing
    if (e.target.value === confirmPassword) {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);

    // Clear error if passwords match while typing
    if (password === e.target.value) {
      setError("");
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!passwordValidations.length || !passwordValidations.complexity || passwordValidations.strength === "Weak") {
      setError("Please ensure your password meets the required criteria.");
      return;
    }

    try {
      const response = await vendorAuthService.resetPassword(email, password );
      if (response?.status === true) {
        ToastService.success("Password reset successfully.");
        setTimeout(() => {
          navigate("/vendor/auth/login");
        }, 1000);
      } else {
        setError(response?.data?.error || "Failed to reset password.");
      }
    } catch (error) {
      setError(error?.response?.data?.data?.error || "Something went wrong. Please try again.");
    }
  };

  // Handle Enter key press to submit form
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePasswordSubmit(e); // Trigger form submit when Enter is pressed
    }
  };

  return (
    <Paper className="inner-card" elevation={5}>
      <Typography variant="h5" color="#5E5ACF" fontWeight="bold">
        Set New Password
      </Typography>
      <form onSubmit={handlePasswordSubmit} onKeyDown={handleKeyDown}>
        <TextField
          fullWidth
          margin="normal"
          label="New Password"
          type={showPassword ? "text" : "password"} // Toggle between text and password
          variant="outlined"
          value={password}
          onChange={handlePasswordChange}
          error={!!error}
          helperText={error}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => setShowPassword(!showPassword)} // Toggle show/hide password
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"} // Toggle between text and password
          variant="outlined"
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)} // Toggle show/hide confirm password
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />

        {/* Password Validation Messages */}
        <Box className="password-validation">
          <Typography variant="body2" className="validationtext">
            <span
              style={{
                color: passwordValidations.length ? "green" : "red",
              }}
            >
              {passwordValidations.length ? "✓" : "✗"}
            </span>{" "}
            At least 8 characters
          </Typography>
          <Typography variant="body2" className="validationtext">
            <span
              style={{
                color: passwordValidations.complexity ? "green" : "red",
              }}
            >
              {passwordValidations.complexity ? "✓" : "✗"}
            </span>{" "}
            Letters, numbers & special characters (e.g. ~!@#$%^&)
          </Typography>
          <Typography variant="body2" className="validationtext">
            <span
              style={{
                color:
                  passwordValidations.strength === "Strong"
                    ? "green"
                    : passwordValidations.strength === "Medium"
                    ? "orange"
                    : "red",
              }}
            >
              {passwordValidations.strength === "Strong" ? "✓" : "✗"}
            </span>{" "}
            Password Strength: {passwordValidations.strength}
          </Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          className="btn-submit"
          sx={{ mt: 2, color: "black", fontWeight: "bold" }}
        >
          Save New Password
        </Button>
      </form>
    </Paper>
  );
}

export default SetNewPassword;
